import { z } from "zod";

/**
 * Who someone is to us: a customer, or staff.
 *
 * `admin` is a human answering "is the system working" and, when a customer
 * emails in, changing something on their account. Both need to see across
 * every customer, which is the whole reason /ops exists and the whole reason
 * it must never be reachable by a customer.
 *
 * There was a third role, `ops`, which held everything `admin` holds except
 * `users:manage`. It was dropped because the separation was never used: one
 * person runs this, every staff account created was an `admin`, and the
 * `ops` branch of every gate was therefore untested in production while
 * still being one of two answers every capability check could return.
 *
 * Deliberately not a hierarchy of numbers: a numeric rank invites
 * `role >= 2` comparisons at call sites, and the day a role lands somewhere
 * between two existing ones, every one of those comparisons is silently
 * wrong.
 */
export const Role = z.enum(["customer", "admin"]);
export type Role = z.infer<typeof Role>;

/**
 * What a role may do, named after the act rather than the route, so moving
 * or renaming an endpoint does not change who can reach it.
 */
export const Capability = z.enum([
  "alerts:read",
  "alerts:write",
  "matches:read",
  "devices:write",
  /** The cross-customer operator view: every alert, every run, every failure. */
  "ops:read",
  /**
   * ACTING on the fleet: burning an identity's sessions, minting a new one.
   *
   * Split from `ops:read` because the two are not the same risk and were
   * being treated as if they were. The burn endpoint shipped gated on
   * `ops:read`, which meant "may look at the dashboard" also meant "may
   * destroy every working credential the fleet has" -- and a fleet with no
   * sessions is the outage this system exists to avoid (observed: eleven
   * consecutive `no_session` runs after the last session expired with
   * nothing to replace it). Reading is safe to hand out widely; the two
   * buttons that change the fleet's ability to collect are not.
   */
  "ops:write",
  /** Changing another account -- its role, its plan. */
  "users:manage",
]);
export type Capability = z.infer<typeof Capability>;

const CUSTOMER_CAPABILITIES = [
  "alerts:read",
  "alerts:write",
  "matches:read",
  "devices:write",
] as const satisfies readonly Capability[];

/**
 * The permission map, in code and not in a table.
 *
 * A `permissions` table earns its place when customers define their own
 * roles. They do not, and until they do a table costs a join on every
 * request and turns "who can reach /ops" into a question that can only be
 * answered by querying production. Here it is answered by reading this file,
 * it changes under code review, and it is versioned with the code that
 * depends on it -- a deploy can never disagree with its own permissions.
 *
 * `satisfies` rather than a bare annotation so adding a member to `Role`
 * fails this file to compile until the new role's capabilities are stated.
 * The alternative -- a partial map with a default -- means a forgotten role
 * silently inherits someone else's access.
 */
export const ROLE_CAPABILITIES = {
  customer: CUSTOMER_CAPABILITIES,
  /**
   * An admin gets the fleet view ON TOP OF the customer capabilities, not
   * instead of them.
   *
   * That is not generosity, it is accuracy. The customer routes are gated
   * on having a session and are scoped to the caller's own id, so an admin
   * account can already read its own (empty) alert list; listing only the
   * staff capabilities here would state a restriction that nothing
   * enforces, and a permission map that describes rules the code does not
   * apply is worse than no map -- it is the one people cite in review.
   */
  admin: [...CUSTOMER_CAPABILITIES, "ops:read", "ops:write", "users:manage"],
} as const satisfies Record<Role, readonly Capability[]>;

/**
 * Whether a set of granted capabilities contains a required one.
 *
 * The predicate every gate is built on, exported in its own right because
 * "does holding `ops:read` let you through an `ops:write` gate" is a
 * question about CAPABILITIES, not about roles -- and no role here answers
 * it, since `admin` deliberately holds both. Without this, the rule that
 * the two are independent could only be asserted by reading the map.
 */
export function grants(granted: readonly Capability[], required: Capability): boolean {
  return granted.includes(required);
}

/**
 * The only way to ask whether a role may do something.
 *
 * Call sites say `can(role, "ops:read")` and never `role === "admin"`. That
 * is what made dropping the `ops` role a change to the map above and to a
 * migration, rather than a hunt for every equality check in the codebase --
 * and it is the reason the capabilities keep their `ops:` names: they are
 * named after the ACT, and the act did not change when the role did.
 */
export function can(role: Role, capability: Capability): boolean {
  return grants(ROLE_CAPABILITIES[role], capability);
}

/**
 * Whether `actual` grants at least everything `required` grants.
 *
 * This is what `requireRole("customer")` means, and why it is not written
 * as `role === "customer"`: an admin is meant to be able to use their own
 * alerts, and an equality check would lock them out -- silently, and only
 * for the people least likely to file a bug about it. Asking whether one
 * role's capabilities contain another's keeps that answer derived from the
 * map above, so it stays right as the map changes.
 */
export function roleSatisfies(actual: Role, required: Role): boolean {
  const granted: readonly Capability[] = ROLE_CAPABILITIES[actual];
  return ROLE_CAPABILITIES[required].every((capability) => granted.includes(capability));
}

/**
 * What a customer is paying for. Cadence is the product's plan lever (see
 * AlertCadence in alert.ts): every alert run is one request against a fleet
 * with a hard ceiling, so "how often" is literally what is being sold.
 */
export const Plan = z.enum(["free", "pro"]);
export type Plan = z.infer<typeof Plan>;

/**
 * The fastest each plan is allowed to poll, in seconds.
 *
 * This table is the price list. One alert run is one request against a fleet
 * whose per-identity budget is 30 requests a minute, so a pro alert at 60s
 * costs 1/30th of an identity and an identity carries about thirty of them.
 * Changing a number here changes both what is sold and how many customers an
 * account can hold, which is why it lives in one place rather than being
 * spread across a validator and a worker.
 *
 * Free is deliberately too slow to snipe with. It exists to show the product
 * working, not to be a worse version of the paid one: fifteen minutes finds
 * the sofa, and finds it after somebody on pro already messaged the seller.
 */
export const PLAN_MIN_INTERVAL_SECONDS: Record<Plan, number> = {
  free: 900,
  pro: 60,
};

/**
 * The interval this plan will actually run at.
 *
 * Asked for by both sides and deliberately so. The API uses it to refuse a
 * request it cannot honour, with the floor in the message; the engine uses it
 * again at claim time, because a plan can lapse long after the alert was
 * saved and the row keeps whatever interval it was created with. Neither one
 * can be trusted alone: validation without clamping lets a downgrade keep a
 * fast alert forever, and clamping without validation silently ignores what
 * the customer asked for.
 */
export function effectiveIntervalSeconds(plan: Plan, requested: number): number {
  return Math.max(requested, PLAN_MIN_INTERVAL_SECONDS[plan]);
}

/**
 * Where the money is taken, kept separate from the plan so a customer who
 * moves from the App Store to the web keeps their plan and changes only
 * this. `none` is the honest state for an account that has never paid --
 * nullable would make every read ask "is null free, or is it unknown?".
 */
export const BillingProvider = z.enum(["none", "revenuecat", "stripe"]);
export type BillingProvider = z.infer<typeof BillingProvider>;

/**
 * The subscription lifecycle, as the union of what the stores and Stripe
 * actually report -- not active/cancelled, which is the shape that has to be
 * retrofitted the first week real money arrives.
 *
 * The expensive distinction is between the states where the money has not
 * arrived *yet* and the states where it arrived and went back:
 *
 *   in_grace_period    Apple/Google: renewal failed, the store is retrying
 *                      and the customer KEEPS ENTITLEMENT meanwhile.
 *   in_billing_retry   Apple's retry window after grace expires; Stripe's
 *                      `past_due`. Still recoverable, and cutting someone
 *                      off here churns a customer whose card simply expired.
 *   canceled           Auto-renew is off; the period already paid for is
 *                      still running. Access continues until `expired`.
 *   expired            The period ended. This, not `canceled`, is the state
 *                      that stops access.
 *   paused             Google Play's subscription pause. Voluntary, not a
 *                      failure, and not entitled.
 *   refunded           The money was given back.
 *   chargeback         The bank took it back. Held separately from
 *                      `refunded` because the two need different handling
 *                      in fraud review even though both revoke instantly.
 *
 * Collapsing grace and retry into a single "past_due" is the mistake that
 * cannot be undone later: once the distinction is gone from the column, the
 * history is gone with it.
 */
export const PlanStatus = z.enum([
  "trialing",
  "active",
  "in_grace_period",
  "in_billing_retry",
  "canceled",
  "expired",
  "paused",
  "refunded",
  "chargeback",
]);
export type PlanStatus = z.infer<typeof PlanStatus>;

/**
 * Whether a status still entitles someone to the paid product.
 *
 * Written as an explicit list of the states that DO grant access, so a
 * status added to the enum later is denied until somebody decides
 * otherwise. A default-allow predicate (`status !== "expired"`) would hand
 * a brand-new failure state full access on the day it is introduced.
 *
 * Exported as a list as well as a predicate because the engine needs it in
 * SQL: `dueAlerts` leaves an unentitled account's alerts out of the result
 * entirely rather than fetching and discarding them. One list, read two
 * ways, so a status can never be entitled in the worker and not in the API.
 *
 * No billing provider is wired yet, so in practice everything is `active`.
 */
export const ENTITLED_STATUSES: readonly PlanStatus[] = [
  "trialing",
  "active",
  // Both are "the money is late", not "the money is gone". A reseller whose
  // card expired on a Sunday must still get their alerts on Monday.
  "in_grace_period",
  "in_billing_retry",
  // Auto-renew off, but the period they already paid for is not over.
  "canceled",
];

export function planGrantsAccess(status: PlanStatus): boolean {
  return ENTITLED_STATUSES.includes(status);
}

/**
 * What register and login accept.
 *
 * The password floor is length and nothing else. Composition rules
 * ("one symbol, one digit") measurably push people toward `Password1!` and
 * away from a passphrase, and the upper bound exists because the hash cost
 * is paid on an unauthenticated endpoint -- without it a megabyte of
 * password is a free way to occupy a CPU.
 */
export const Credentials = z.object({
  email: z.string().email().max(254),
  password: z.string().min(12).max(200),
});
export type Credentials = z.infer<typeof Credentials>;

/**
 * What LOGIN accepts, and deliberately looser than `Credentials`.
 *
 * Sign-in must not apply the registration policy. Two reasons, and the
 * second is the serious one:
 *
 *   - Raising the minimum length later would otherwise lock out every
 *     customer whose password predates the change, with a validation error
 *     that tells them their own password is invalid.
 *   - A 400 short-circuits the route before the rate limiter records
 *     anything. Enforcing a 12-character floor here would hand an attacker
 *     an unmetered channel: guesses under the floor cost nothing and never
 *     count toward the lockout.
 *
 * The maximum stays, because the argon2id cost is paid on an
 * unauthenticated endpoint and an unbounded password is a free way to
 * occupy a CPU.
 */
export const LoginCredentials = z.object({
  email: z.string().email().max(254),
  password: z.string().min(1).max(200),
});
export type LoginCredentials = z.infer<typeof LoginCredentials>;

/**
 * A user as the API is willing to describe one.
 *
 * This type is the reason `GET /auth/me` cannot leak a password hash: the
 * route builds this shape explicitly instead of returning the database row,
 * so a column added to `users` later is invisible to clients until somebody
 * adds it here on purpose.
 */
export const PublicUser = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: Role,
  plan: Plan,
  planStatus: PlanStatus,
  createdAt: z.coerce.date(),
});
export type PublicUser = z.infer<typeof PublicUser>;
